/**
 * Definitions learned in a chat: save, skip, edit, delete and bring back.
 *
 * Every call names the chat event the definition came from and its item id,
 * and answers with the item's new state (`{ item }`). Contract:
 * docs/features/knowledge/kb-v2-contract.md.
 */
import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import type { LearnedItem, LearnedMatch } from '../types/definitions';

export interface SaveLearnedBody {
  name: string;
  definition: string;
  mode: 'new' | 'update';
  target_node_id?: string;
}

/** A failed call. `duplicate` is set when a definition with that name already exists. */
export class DefinitionRequestError extends Error {
  status: number;
  duplicate?: LearnedMatch;

  constructor(message: string, status: number, duplicate?: LearnedMatch) {
    super(message);
    this.name = 'DefinitionRequestError';
    this.status = status;
    this.duplicate = duplicate;
  }
}

function headers(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function itemUrl(eventId: string, itemId: string, action = ''): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new DefinitionRequestError('Please sign in again.', 401);
  const base = `${tenantApiPrefix(tid)}/knowledge/learned/${encodeURIComponent(eventId)}/items/${encodeURIComponent(itemId)}`;
  return buildApiUrl(action ? `${base}/${action}` : base);
}

const FAILED: Record<string, string> = {
  save: 'We couldn’t save this definition. Please try again.',
  'dont-save': 'We couldn’t update this definition. Please try again.',
  edit: 'We couldn’t save your changes. Please try again.',
  delete: 'We couldn’t delete this definition. Please try again.',
  restore: 'We couldn’t bring this definition back. Please try again.',
};

async function send(
  eventId: string,
  itemId: string,
  action: 'save' | 'dont-save' | 'edit' | 'delete' | 'restore',
  body?: unknown,
): Promise<LearnedItem> {
  const resp = await fetch(itemUrl(eventId, itemId, action === 'edit' ? '' : action), {
    method: action === 'edit' ? 'PUT' : 'POST',
    headers: headers(),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    const detail = data && typeof data === 'object' ? (data as { detail?: unknown }).detail : null;
    if (resp.status === 409 && detail && typeof detail === 'object' && (detail as { code?: string }).code === 'duplicate') {
      const match = (detail as { match?: LearnedMatch }).match;
      const name = match?.name || (body as SaveLearnedBody | undefined)?.name || 'this';
      throw new DefinitionRequestError(`A definition called “${name}” already exists.`, 409, match);
    }
    if (resp.status === 403) {
      throw new DefinitionRequestError('You don’t have permission to change the knowledge base.', 403);
    }
    throw new DefinitionRequestError(typeof detail === 'string' ? detail : FAILED[action], resp.status);
  }
  const item = data && typeof data === 'object' ? (data as { item?: LearnedItem }).item : undefined;
  if (!item) throw new DefinitionRequestError(FAILED[action], resp.status);
  return item;
}

export const saveLearnedDefinition = (eventId: string, itemId: string, body: SaveLearnedBody) =>
  send(eventId, itemId, 'save', body);

export const dontSaveLearnedDefinition = (eventId: string, itemId: string) =>
  send(eventId, itemId, 'dont-save');

export const editLearnedDefinition = (eventId: string, itemId: string, body: { name: string; definition: string }) =>
  send(eventId, itemId, 'edit', body);

export const deleteLearnedDefinition = (eventId: string, itemId: string) =>
  send(eventId, itemId, 'delete');

export const restoreLearnedDefinition = (eventId: string, itemId: string) =>
  send(eventId, itemId, 'restore');
